import React from "react";
import { batch, connect } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { ApiService, ProjectDocumentService } from "../../api";
import {
	environmentSwitch,
	Flags,
	hasCompanyPermission,
	hasPermission,
	isSegFeatureEnabled,
	Permissions,
	SegFlags,
	translate,
} from "../../common/providers";
import { exportView, NavUtils } from "../../navigation";
import {
	HorizontalDivider,
	SideDrawerMain,
	withWebsocket,
	CustomButton,
	DocumentAutocompleteSearch,
	FiltersWrapper,
	CommonFilters,
	icon,
	withtrackEvent,
} from "../../common/components";
import {
	DirectoryLocation,
	DocumentCenterMenu,
	DocumentCenterSidePanel,
	DocumentsStatusInformationBanner,
	HelperBox,
	SaveFileDialog,
	UploadDialog,
	DistributionNotificationSidePanel,
	DocumentCenterTable,
} from "./components";
import DistributionAlertService from "../../api/services/distribution-alert-service";
import UnnotifiedDocumentsAlert from "../../navigation/components/menus/UnnotifiedDocumentsAlert";
import styles from "./DocumentCenter.module.css";
import { debounce, isAllowedFile, isNonEmptyArray } from "../../common/utils";
import {
	getContent,
	getCost,
	getLocation,
	getTree,
	ROOT_DIR,
	setHasActionPermission,
	setHasReceivedModifications,
	setLoadingLocation,
	setOpenUploadDialog,
	setSearchDocumentId,
	setUploadProgress,
	setPage,
	updateAnalysis,
	setImports,
	setImportProgress,
	getStatusCounters,
	setAnalyzingDocuments,
	setFailedDocuments,
	setHadDocumentsToAnalyze,
	setDocCenterFilters,
	markAsCandidate,
} from "./slice/document-center-slice";
import { onBeforeUnloadHandler } from "./utils/utils";
import PrecedenceSidePanel from "./components/precedence-side-panel/PrecedenceSidePanel";
import { DOCUMENT_STATUS } from "./constants/constants";

const debouncedFunction = debounce((func) => func(), 500);
const qualityAndProgress = [
	{ label: "document-center.filter.analysis-in-progress", value: { status: ["IN_PROGRESS", "PENDING"] } },
	{ label: "document-center.filter.analysis-failed", value: { status: ["FAILED", "TIMEOUT"] } },
	{
		label: "document-center.filter.qood-quality",
		value: { score: { min: 85, max: 100 }, status: ["COMPLETED"] },
		iconName: icon.faCircle,
		iconPlacement: "left",
		color: "var(--lime-500)",
	},
	{
		label: "document-center.filter.average-quality",
		value: { score: { min: 30, max: 84 }, status: ["COMPLETED"] },
		iconName: icon.faCircle,
		iconPlacement: "left",
		color: "var(--amber-500)",
	},
	{
		label: "document-center.filter.insufficient-quality",
		value: { score: { min: 0, max: 29 }, status: ["COMPLETED"] },
		iconName: icon.faCircle,
		iconPlacement: "left",
		color: "var(--red-500)",
	},
];

const queryParam = "directory";

const mapStateToProps = ({ context, documentCenter }) => ({
	projectId: context.project.id,
	userId: context.user.id,
	currentDirectoryId: documentCenter.currentDirectoryId,
	isUploading: documentCenter.isUploading,
	page: documentCenter.page,
	limit: documentCenter.limit,
	sort: documentCenter.sort,
	docCenterFilters: documentCenter.docCenterFilters,
	direction: documentCenter.direction,
	hasActionPermission: documentCenter.hasActionPermission,
	hasReceivedModifications: documentCenter.hasReceivedModifications,
	bulkVersioningInProgress: documentCenter.bulkVersioningInProgress,
	relaunchSelectedDocument: documentCenter.relaunchSelectedDocument,
	openUploadDialog: documentCenter.openUploadDialog,
	imports: documentCenter.imports,
	analyzingDocuments: documentCenter.analyzingDocuments,
	failedDocuments: documentCenter.failedDocuments,
	hideDocStatusInfoBanner: documentCenter.hideDocStatusInfoBanner,
	isLoadingContent: documentCenter.isLoadingContent,
	documents: documentCenter.currentDirectoryContent.contents,
	documentsCount: documentCenter.cost?.documentsCount,
	portfolioInfo: context.portfolioInfo,
});

const mapDispatchToProps = (dispatch) => ({
	onSetImports: (files) => dispatch(setImports(files)),
	onGetLocation: (projectId, directoryId, token) => dispatch(getLocation({ projectId, directoryId, token })),
	onGetTree: (projectId, token) => dispatch(getTree({ projectId, token })),
	onGetContent: ({ projectId, directoryId, page, limit, sort, direction, token }) =>
		dispatch(getContent({ projectId, directoryId, page, limit, sort, direction, token })),
	onGetStatusCounters: ({ projectId, token }) => dispatch(getStatusCounters({ projectId, token })),
	onSetHasActionPermission: (hasPermissionToPerformAction) =>
		dispatch(setHasActionPermission(hasPermissionToPerformAction)),
	onSetHasReceivedModification: (hasReceivedModifications) =>
		dispatch(setHasReceivedModifications(hasReceivedModifications)),
	onUpdateAnalysis: (payload) => dispatch(updateAnalysis(payload)),
	onSetOpenUploadDialog: (openUploadDialog) => dispatch(setOpenUploadDialog(openUploadDialog)),
	onSetUploadProgress: (payload) => dispatch(setUploadProgress(payload)),
	onSetImportProgress: (payload) => dispatch(setImportProgress(payload)),
	onSetSearchDocumentId: (payload) => dispatch(setSearchDocumentId(payload)),
	onSetLoadingLocation: (payload) => dispatch(setLoadingLocation(payload)),
	onSetAnalyzingDocuments: (payload) => dispatch(setAnalyzingDocuments(payload)),
	onSetHadDocumentsToAnalyze: (payload) => dispatch(setHadDocumentsToAnalyze(payload)),
	onSetFailedDocuments: (payload) => dispatch(setFailedDocuments(payload)),
	onSetDocCenterFilters: (payload) => dispatch(setDocCenterFilters(payload)),
	onGetCost: (projectId, token) => dispatch(getCost({ projectId, token })),
	onSetPage: (payload) => dispatch(setPage(payload)),
	onMarkAsCandidate: (payload) => dispatch(markAsCandidate(payload)),
});

class DocumentCenter extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			isLoadingDocuments: false,
			searchDocument: {},
			searchDocumentInputValue: "",
			openNewFolderDialog: false,
			openDrawer: true,
			openSidePanel: { id: null, open: false, type: null, status: null },
			openDistributionNotificationSidePanel: false,
			unnotifiedDocCount: 0,
			unnotifiedAlertData: null,
			openPrecedenceSidePanel: false,
		};
		this.cancelTokenSource = ApiService.getCancelTokenSource();
		this.menuButtonDropDownRef = React.createRef();
		this.helperBoxRef = React.createRef();
		this.sidePanelInitialized = false;
	}

	componentDidMount() {
		const {
			location,
			currentDirectoryId,
			onSetHasActionPermission,
			onGetCost,
			projectId,
			trackEvent,
			portfolioInfo,
		} = this.props;
		const documentTitle = translate("document-center.document.title");
		document.title = documentTitle;
		trackEvent("Document_visit");
		const directoryId = NavUtils.getQueryParams(location).get(queryParam) ?? currentDirectoryId;
		this.handleRedirectToDirectory(directoryId);
		const hasActionPermission = environmentSwitch({
			main:
				hasPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]) &&
				hasCompanyPermission([Permissions.PROJECT_LEADER]),
			veolia:
				(hasPermission([Permissions.COMPANY_ADMIN]) && hasPermission([Permissions.PROJECT_LEADER])) ||
				(hasPermission([Permissions.COMPANY_MANAGER]) && hasPermission([Permissions.PROJECT_MANAGER])),
		});
		onSetHasActionPermission(hasActionPermission);
		onGetCost(projectId, this.cancelTokenSource.token);
		this.handleGetAllDocVersionCandidateCount();
		this.subscribeSocket(hasActionPermission);
		const isCertificateProject = portfolioInfo?.name === "Certificats";
		const isNotificationDiffusionEnabled =
			environmentSwitch({ veolia: true, main: false }) && isSegFeatureEnabled(SegFlags.NOTIFICATION_DIFFUSION);
		if (hasActionPermission && !isCertificateProject && isNotificationDiffusionEnabled) {
			this.fetchUnnotifiedAlert();
		}
		this.tryInitSidePanelFromUrl();
	}

	componentDidUpdate(prevProps) {
		const { location, hasReceivedModifications, bulkVersioningInProgress, navigate, documents, isLoadingContent } =
			this.props;
		const { hasCleanedUrl } = this.state;
		if (prevProps.location.search !== location.search) {
			const directoryId = NavUtils.getQueryParams(location).get(queryParam) || ROOT_DIR;
			this.handleRedirectToDirectory(directoryId);
		}
		if (prevProps !== hasReceivedModifications && hasReceivedModifications) {
			this.debouncedRefresh();
		}
		if (prevProps.bulkVersioningInProgress !== bulkVersioningInProgress && bulkVersioningInProgress === false) {
			this.handleGetAllDocVersionCandidateCount();
		}
		if (hasCleanedUrl) {
			navigate(location.pathname, { replace: true });
			this.setState({ hasCleanedUrl: true });
		}
		if (prevProps.documents !== documents || prevProps.isLoadingContent !== isLoadingContent) {
			this.tryInitSidePanelFromUrl();
		}
	}

	componentWillUnmount() {
		ApiService.cancelTokens(this.cancelTokenSource);
	}

	subscribeSocket = (hasActionPermission) => {
		const { subscribeWS, projectId } = this.props;
		subscribeWS(`/topic/document-center/projects/${projectId}/refresh`, this.handleRefreshMessage);
		if (hasActionPermission) {
			subscribeWS(`/topic/document-center/projects/${projectId}/analysis`, this.handleAnalysisMessage);
		}
		if (isSegFeatureEnabled(SegFlags.VERSIONS_BULK)) {
			subscribeWS(`/topic/doc-candidate/projects/${projectId}/refresh`, this.handleCandidateRefreshMessage);
		}
		if (isSegFeatureEnabled(SegFlags.VERSIONS)) {
			subscribeWS(`/topic/doc-candidate/projects/${projectId}/refresh`, this.handleCandidateRefreshMessage);
		}
	};

	tryInitSidePanelFromUrl = () => {
		if (this.sidePanelInitialized) {
			return;
		}
		const { documents, isLoadingContent, location } = this.props;
		if (isLoadingContent || !isNonEmptyArray(documents)) {
			return;
		}
		const params = new URLSearchParams(location.search);
		if (params.get("isOpenSP") !== "true") {
			return;
		}
		const documentId = Number(params.get("documentId"));
		const exists = documents.some((d) => d.id === documentId);
		if (!exists) {
			return;
		}

		this.setState({
			openSidePanel: {
				id: documentId,
				open: true,
				type: params.get("documentType"),
				status: params.get("documentStatus"),
			},
		});

		this.sidePanelInitialized = true;
	};

	handleGetAllDocVersionCandidateCount = () => {
		const { projectId } = this.props;
		if (
			isSegFeatureEnabled(SegFlags.VERSIONS_BULK) &&
			hasPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]) &&
			hasCompanyPermission([Permissions.PROJECT_LEADER])
		) {
			ProjectDocumentService.getAllDocVersionCandidateCount({ projectId }, this.cancelTokenSource.token)
				.then((data) => this.setState({ hasBulkVersionCandidates: data.value }))
				.catch(console.error);
		}
	};

	fetchUnnotifiedAlert = () => {
		const { projectId } = this.props;
		DistributionAlertService.getUnnotifiedAlert(projectId, this.cancelTokenSource.token)
			.then((data) => {
				this.setState({
					unnotifiedDocCount: data.unnotifiedDocCount || 0,
					unnotifiedAlertData: data,
				});
			})
			.catch((err) => console.error(err));
	};

	handleRefreshMessage = ({ userId, directoryId, reason }) => {
		const { userId: currentUserId, currentDirectoryId, onSetHasReceivedModification } = this.props;
		const originUser = +userId ? +userId : 0;
		const targetedDirectory = +directoryId ? +directoryId : ROOT_DIR;
		if (currentUserId === originUser && currentDirectoryId === targetedDirectory && reason !== "NEW_DOCUMENT") {
			return;
		}
		onSetHasReceivedModification(true);
	};

	handleCandidateRefreshMessage = ({ newDocId, reason }) => {
		const { hasBulkVersionCandidates } = this.state;
		const { onMarkAsCandidate, onSetHasReceivedModification } = this.props;
		if (reason === "UPDATE") {
			onSetHasReceivedModification(true);
		} else if (newDocId) {
			onMarkAsCandidate({ id: parseInt(newDocId, 10) });
			this.setState({ hasBulkVersionCandidates: hasBulkVersionCandidates + 1 });
		}
	};

	handleAnalysisMessage = ({ id, name, type, status, progression, pages, parsingScore, reqs, versioningScore }) => {
		const {
			onUpdateAnalysis,
			onSetAnalyzingDocuments,
			onSetHadDocumentsToAnalyze,
			onSetFailedDocuments,
			analyzingDocuments,
			failedDocuments,
		} = this.props;
		if (analyzingDocuments > 0) {
			onSetHadDocumentsToAnalyze(true);
		}
		if (status === DOCUMENT_STATUS.COMPLETED && +progression) {
			onSetAnalyzingDocuments(analyzingDocuments - 1);
		}
		if (status === DOCUMENT_STATUS.FAILED && +progression) {
			onSetFailedDocuments(failedDocuments + 1);
		}
		onUpdateAnalysis({
			id: +id,
			name,
			type,
			status,
			progression: +progression,
			pages: +pages,
			parsingScore: status === DOCUMENT_STATUS.COMPLETED ? +parsingScore : null,
			reqs: +reqs,
			versioningScore: +versioningScore,
		});
	};

	handleRedirectToDirectory = (directoryId) => {
		const { navigate, projectId, onGetLocation } = this.props;
		onGetLocation(projectId, directoryId, this.cancelTokenSource.token)
			.then(unwrapResult)
			.catch(() => {
				const id = directoryId ?? ROOT_DIR;
				const url =
					id === ROOT_DIR
						? `/projects/${projectId}/documents`
						: `/projects/${projectId}/documents?${queryParam}=${id}`;
				navigate(url);
			});
	};

	debouncedSearch = debounce((name) => {
		const { projectId } = this.props;
		ProjectDocumentService.search({ projectId }, { name }, this.cancelTokenSource.token)
			.then((data) => this.setState({ searchDocument: data }))
			.catch(console.error)
			.finally(() => this.setState({ isLoadingDocuments: false }));
	}, 300);

	debouncedRefresh = debounce(() => {
		const {
			projectId,
			currentDirectoryId,
			onGetLocation,
			onGetCost,
			onSetHasReceivedModification,
			onGetTree,
			onGetContent,
			onGetStatusCounters,
			page,
			limit,
			sort,
			direction,
		} = this.props;
		const { token } = this.cancelTokenSource;
		debouncedFunction(() => {
			onGetStatusCounters({ projectId, token });
		});
		onGetLocation(projectId, currentDirectoryId, token);
		onGetTree(projectId, token);
		onGetContent({
			projectId,
			directoryId: currentDirectoryId,
			page,
			limit,
			sort,
			direction,
			token: this.cancelTokenSource.token,
		});
		onSetHasReceivedModification(false);
		onGetCost(projectId, this.cancelTokenSource.token);
	}, 1000);

	handleResetSearchInput = () => {
		this.setState({ isLoadingDocuments: false, searchDocument: [], searchDocumentInputValue: "" });
		this.handleResetSearchDocumentId();
	};

	handleResetSearchDocumentId = () => {
		const { onSetSearchDocumentId } = this.props;
		onSetSearchDocumentId(null);
	};

	handleSearchDocument = (name) => {
		const { onSetPage } = this.props;
		if (!name) {
			this.setState({ isLoadingDocuments: false, searchDocument: [], searchDocumentInputValue: name });
			return;
		}
		onSetPage(0);
		this.setState({ isLoadingDocuments: true, searchDocumentInputValue: name });
		this.debouncedSearch(name);
	};

	handleOpenSidePanel = ({ open, id, type, status }) => this.setState({ openSidePanel: { id, open, type, status } });

	handleCloseSidePanel = () => this.setState({ openSidePanel: { id: null, open: false, type: null, status: null } });

	handleNavigateToFolder = ({ id: directoryId, documentId, documentStatus, tenderType }) => {
		const { projectId, navigate, onSetSearchDocumentId, onSetLoadingLocation } = this.props;
		if (documentId) {
			this.setState(() => ({
				openSidePanel: {
					id: documentId,
					open: true,
					type: tenderType,
					status: documentStatus,
				},
			}));
		}
		const id = directoryId ?? ROOT_DIR;
		const url =
			id === ROOT_DIR
				? `/projects/${projectId}/documents`
				: `/projects/${projectId}/documents?${queryParam}=${id}`;
		navigate(url);
		batch(() => {
			onSetLoadingLocation(true);
			onSetSearchDocumentId(null);
		});
		this.setState({ isLoadingDocuments: false, searchDocument: [], searchDocumentInputValue: "" });
	};

	handleOpenNewFolderDialog = () => {
		if (this.menuButtonDropDownRef.current) {
			this.menuButtonDropDownRef.current.onClose();
		}
		this.setState((prev) => ({ openNewFolderDialog: !prev.openNewFolderDialog }));
	};

	handleImportFile = (targetFiles) => {
		const {
			projectId,
			currentDirectoryId,
			onSetOpenUploadDialog,
			onSetUploadProgress,
			onSetImports,
			imports,
			onSetHadDocumentsToAnalyze,
			onSetImportProgress,
			onSetHasReceivedModification,
			trackEvent,
		} = this.props;
		if (this.menuButtonDropDownRef.current) {
			this.menuButtonDropDownRef.current.onClose();
		}
		const files = Object.keys(targetFiles).map((key) => targetFiles[key]);
		const payload = {
			directoryId: currentDirectoryId === ROOT_DIR ? null : currentDirectoryId,
		};
		window.addEventListener("beforeunload", onBeforeUnloadHandler);
		const batchId = imports.length;
		onSetImports(
			files.map(({ lastModified, name, size, type }) => ({
				name,
				type,
				lastModified,
				size,
			}))
		);
		const startTime = new Date();
		if (files.some((file) => isAllowedFile(file.type))) {
			onSetHadDocumentsToAnalyze(false);
		}
		ProjectDocumentService.uploadDocuments(
			{ projectId },
			files,
			payload,
			(metadata) => {
				const { loaded, total } = metadata;
				const timeElapsed = new Date() - startTime;
				const uploadSpeed = loaded / (timeElapsed / 1000);
				const progression = (loaded / total) * 100;
				onSetImportProgress({ progression, batchId, uploadSpeed, total });
				onSetUploadProgress(progression);
			},
			this.cancelTokenSource.token
		)
			.then(() => {
				onSetHasReceivedModification(true);
				trackEvent("Document_Upload_Start");
			})
			.catch((err) => {
				console.error(err);
				onSetImportProgress({ progression: null, batchId, uploadSpeed: 0, total: 0 });
			})
			.finally(() => {
				window.removeEventListener("beforeunload", onBeforeUnloadHandler);
			});
		onSetOpenUploadDialog(true);
	};

	getDrawerComponent = () => (
		<DocumentCenterMenu
			onClickDirectory={this.handleNavigateToFolder}
			onImportFile={this.handleImportFile}
			onOpenNewFolderDialog={this.handleOpenNewFolderDialog}
		/>
	);

	handleOpenDrawer = () => this.setState((prev) => ({ openDrawer: !prev.openDrawer }));

	handleOpenPrecedenceSidePanel = () =>
		this.setState((prev) => ({ openPrecedenceSidePanel: !prev.openPrecedenceSidePanel }));

	handleOpenDistributionNotificationSidePanel = () =>
		this.setState((prev) => ({
			openDistributionNotificationSidePanel: !prev.openDistributionNotificationSidePanel,
		}));

	handleFilterChange = (newFilters) => {
		const { onSetDocCenterFilters } = this.props;
		onSetDocCenterFilters(newFilters);
	};

	handleHideUnnotifiedAlert = () => {
		this.setState({
			unnotifiedDocCount: 0,
			unnotifiedAlertData: null,
		});
	};

	render() {
		const {
			searchDocument,
			searchDocumentInputValue,
			isLoadingDocuments,
			openNewFolderDialog,
			openDrawer,
			openSidePanel,
			openPrecedenceSidePanel,
			hasBulkVersionCandidates,
			openDistributionNotificationSidePanel,
			unnotifiedDocCount,
			unnotifiedAlertData,
		} = this.state;
		const {
			hasActionPermission,
			openUploadDialog,
			analyzingDocuments,
			failedDocuments,
			hideDocStatusInfoBanner,
			docCenterFilters,
			relaunchSelectedDocument,
			documentsCount,
			projectId,
			portfolioInfo,
		} = this.props;
		const isCertificateProject = portfolioInfo?.name === "Certificats";
		const canDistribute = hasActionPermission && !isCertificateProject;
		const isDocStatusInfoBannerVisible =
			(!hideDocStatusInfoBanner && !!failedDocuments) || !!analyzingDocuments || relaunchSelectedDocument;
		const precedenceButton = (
			<CustomButton color="secondary" variant="outlined" onClick={this.handleOpenPrecedenceSidePanel}>
				{translate("document-center.side-panel.manage-document-type")}
			</CustomButton>
		);
		return (
			<>
				{canDistribute &&
					environmentSwitch({ veolia: true, main: false }) &&
					isSegFeatureEnabled(SegFlags.NOTIFICATION_DIFFUSION) && (
						<UnnotifiedDocumentsAlert
							count={unnotifiedDocCount}
							projectName={unnotifiedAlertData?.projectName}
							onDismiss={() => {
								DistributionAlertService.dismissAlert(projectId, this.cancelTokenSource.token)
									.then(() => {
										this.setState({
											unnotifiedDocCount: 0,
											unnotifiedAlertData: null,
										});
									})
									.catch((err) => console.error(err));
							}}
							onNavigate={this.handleOpenDistributionNotificationSidePanel}
						/>
					)}
				<SideDrawerMain
					className={styles.sideDrawer__container}
					contentClassName={styles.sideDrawer__content}
					DrawerComponent={this.getDrawerComponent}
					openDrawer={openDrawer}
					onOpenDrawer={this.handleOpenDrawer}
				>
					{isDocStatusInfoBannerVisible && <DocumentsStatusInformationBanner />}

					{!!hasBulkVersionCandidates && (
						<HelperBox
							ref={this.helperBoxRef}
							hasOpenCandidatesDialog
							className={`${styles.helperBox} ${
								isDocStatusInfoBannerVisible ? styles.helperBox__afterBanner : ""
							}`}
							subTitle={translate(
								"document-center.table.bulk-versioning-dialog.helper-box.doc-center.sub-title"
							)}
							title={translate(
								"document-center.table.bulk-versioning-dialog.helper-box.doc-center.title"
							)}
						/>
					)}
					<div className={styles.main}>
						<div className={styles.main__search}>
							<span className={styles.search__autocomplete}>
								<DocumentAutocompleteSearch
									className={styles.autoComplete}
									documents={searchDocument?.contents || []}
									emptyStateLabel={translate("common:document-autocomplete-search.empty-state")}
									error={false}
									helperText={translate("document-center.search.helper")}
									inputValue={searchDocumentInputValue}
									isLoading={isLoadingDocuments}
									label={translate("document-center.search.title")}
									onReset={this.handleResetSearchDocumentId}
									onSearch={this.handleSearchDocument}
									onSelect={this.handleNavigateToFolder}
								/>
								<FiltersWrapper
									multiline
									components={[
										{
											default: true,
											enabled: true,
											labelKey: "label",
											valueKey: "value",
											component: CommonFilters.QUALITY_PROGRESS,
											dynamicItems: qualityAndProgress,
										},
									]}
									defaultFilters={docCenterFilters}
									onApply={this.handleFilterChange}
								/>
							</span>
							<div className={styles.headerButtons}>
								{isSegFeatureEnabled(SegFlags.PRECEDENCE) &&
									hasCompanyPermission(Permissions.PROJECT_LEADER) &&
									precedenceButton}
								{canDistribute && isSegFeatureEnabled(SegFlags.NOTIFICATION_DIFFUSION) && (
									<CustomButton
										color="primary"
										disabled={!documentsCount}
										variant="contained"
										onClick={this.handleOpenDistributionNotificationSidePanel}
									>
										{translate("document-center.distribution-notification")}
									</CustomButton>
								)}
							</div>
						</div>
						<HorizontalDivider />
						<div className={styles.main__location}>
							<DirectoryLocation onClickDirectory={this.handleNavigateToFolder} />
						</div>
						<HorizontalDivider />
						<div className={styles.main__table}>
							<DocumentCenterTable
								docStatusInfoBannerDisplayed={
									(!hideDocStatusInfoBanner && !!failedDocuments) || !!analyzingDocuments
								}
								hasBulkVersionCandidates={hasBulkVersionCandidates}
								helperBoxRef={this.helperBoxRef}
								onImportFile={this.handleImportFile}
								onNavigateToFolder={this.handleNavigateToFolder}
								onOpenSidePanel={this.handleOpenSidePanel}
								onUpdateCandidateCount={this.handleGetAllDocVersionCandidateCount}
							/>
						</div>
					</div>
				</SideDrawerMain>
				<DocumentCenterSidePanel
					id={openSidePanel.open ? openSidePanel.id : null}
					open={openSidePanel.open}
					status={openSidePanel.status}
					type={openSidePanel.type}
					onClose={this.handleCloseSidePanel}
				/>
				<PrecedenceSidePanel open={openPrecedenceSidePanel} onClose={this.handleOpenPrecedenceSidePanel} />
				{canDistribute && isSegFeatureEnabled(SegFlags.NOTIFICATION_DIFFUSION) && (
					<DistributionNotificationSidePanel
						open={openDistributionNotificationSidePanel}
						onClose={this.handleOpenDistributionNotificationSidePanel}
						onNotificationSent={this.handleHideUnnotifiedAlert}
					/>
				)}
				<SaveFileDialog open={openNewFolderDialog} type="folder" onClose={this.handleOpenNewFolderDialog} />
				{hasActionPermission && <UploadDialog open={openUploadDialog} />}
			</>
		);
	}
}

export { default as documentCenterSlice } from "./slice/document-center-slice";
export default exportView({
	path: "/projects/:projectId/documents/*",
	component: withWebsocket(withtrackEvent(connect(mapStateToProps, mapDispatchToProps)(DocumentCenter))),
	localesPath: "/document-center/locales",
	flag: Flags.DOCUMENT_CENTER,
	segFlag: SegFlags.DOCUMENT_CENTER,
});
